import { AvailabilityStates } from '@coolblue-development/next-availability-state';
import { HemingwayBlocks } from '@coolblue-development/next-hemingway/server';
import { InstallationServiceData } from '@coolblue-development/next-installation-services';
import {
  energyLabelFragment,
  productTypeFragment,
  propertiesFragment
} from '@features/product-page/api/fragments/productFragments';
import { PackageReview } from '@features/product-page/typings';
import { FragmentOf } from 'gql.tada';
import type { ComponentProps, ReactNode } from 'react';

import { Configurator } from '../components/Configurator/types';

export type Product = {
  // SEO
  seo: Seo;
  availableForPickup: AvailableForPickup[];
  brand?: { name?: string; id?: number };
  coolbluesChoice: CoolbluesChoice;
  alternatives: Alternative[];
  listPrice: { includingVat: number } | null;
  prices?: {
    msrp?: {
      includingVat: number;
    };
  };
  specification: EnergyLabelSpecification;
  secondChanceProductMetadata: SecondChanceProduct | null;
  subProducts: SubProducts[];
  subProductTypeId?: number | null;
  energyLabel?: EnergyLabelType;
  id: number;
  name: string;
  shortName: string;
  slug: string;
  availabilityState: AvailabilityStates;
  expectedReleaseDate?: string;
  storeAvailabilities?: StoreAvailability[];
  hasUsbCChargerIncluded: boolean | null;
  isLeasable: boolean | null;
  hasAccessories: boolean;
  navigation: {
    sitemapLinks: SitemapLinks;
  };
  primaryImage: {
    id: string;
  };
  productType: ProductType;
  prosAndCons: {
    pros: string[];
    cons: string[];
  };
  generatedContent: {
    isGenerated: boolean;
    variation: string | null;
  };
  hasProductSupportHub?: boolean;
  manual: {
    manualUrl: string;
    fileSizeBytes: number;
  };
  includedInBox: string[];
  mainText: string;
  properties: Properties;
  recommendedRetailSalesPrice: RecommendedRetailSalesPrice;
  salesPrice: SalesPrice;
  urls: {
    slug: string;
    product: string;
    productReviews: string;
    alternatives?: UrlAlternative[] | null;
  };
  reviewMetaData: Reviews | null;
  services: InstallationServiceData[];
  mediaGallery: MediaGallery;
  promoIcon?: PromoIcon;
  subscription: Subscription;
  promoBoxes: PromoBox[] | null;
  pageLayout: PageLayout | null;
  notice: Notice | null;
  productPlusPromotions?: ProductPlusPromotion[];
  highlights?: string[];
  configurator: Configurator | null;
  personalPrice?: PersonalPrice;
  promotionalContent: PromotionalContent;
  sizeCategoryId: number;
  onDisplayInShowrooms: number[];
  isPartOfClosedCommunity: boolean;
  crossSellOnly: boolean;
  isComposedProduct: boolean;
  conLinkInformation?: ConLinkInformation;
};

export type ProductPlusPromotion = {
  id: string;
  type: string;
  descriptor: string;
  consent?: {
    title: string;
    description: string;
    termsAndConditionsUrl?: string;
  };
  cta?: {
    text: string;
  };
  title: string;
  shortTitle?: string;
  description: string;
  shortDescription?: string;
  products: ProductPlusPromotionProduct[];
};

export type ProductPlusPromotionProduct = {
  priceExcludingVat?: number;
  priceIncludingVat: number;
  listPrice: number;
  id: number;
  slug?: string;
  image: { id: string; label: string; collectionName: string };
  name: string;
  description: string;
};

export type Seo = {
  browserTitle: string;
  metaDescription: string;
  metaDescriptions: {
    default: string;
    timespan?: string;
    cbDelivers: string;
  };
  hrefLangs?: HrefLangs[];
  canonical?: string;
};

export type HrefLangs = {
  url: string;
  hrefLang: string;
};

export type AvailableForPickup = {
  isAvailableForPickup: boolean;
};

export type Subscription = {
  installmentAmount: number;
  oneTimePaymentAmount: number;
};

export type PromoIcon = {
  image: { id: string; collectionName: FormattedCollectionName } | null;
  displayText: string | null;
  explanation: string | null;
};

export type MediaGallery = {
  items: GalleryItem[];
  mainGalleryItem: GalleryItem;
};

export type GalleryItem = ProductYoutubeVideo | ProductImage;

export type ProductYoutubeVideo = {
  __typename: string;
  image: CDNImage;
  id: string;
};

export type ProductImage = {
  __typename: ProductImageTypeName;
  image: CDNImage;
  label: string;
};

export type ProductImageTypeName = 'ProductImage' | 'ProductYoutubeVideo';

export type CDNImage = {
  id: string;
  collectionName: CollectionName;
};

export type CollectionName =
  | 'Content'
  | 'Products'
  | 'Reviews'
  | 'secondchance'
  | 'Temp'
  | 'DeliveryInstallation'
  | 'Animations';

export type FormattedCollectionName = 'content' | 'product' | 'review' | 'second_chance_product';

export type Reviews = {
  averageRating: number;
  count: number;
  reviewAveragePerCategory: ReviewAveragePerCategory[];
  reviewCountPerRating: ReviewCountPerRating[];
  imageSummary: string[];
  reviews: PackageReview[];
};

export type Properties = NonNullable<FragmentOf<typeof propertiesFragment>['properties']>;

export type Property = NonNullable<Properties['summary']>[0];

export type PropertyGroup = NonNullable<Properties['propertyGroups']>[0];

type ReviewAveragePerCategory = {
  averageRating: number;
  name: string;
};

type ReviewCountPerRating = {
  count: number;
  stars: string;
};

export type EnergyLabelType = FragmentOf<typeof energyLabelFragment>['energyLabel'];

export type ProductType = FragmentOf<typeof productTypeFragment>['productType'];

export type EnergyLabelSpecification = {
  absoluteUrl: string;
};

export type RecommendedRetailSalesPrice = {
  includingVat: number;
  excludingVat: number;
};

export type SalesPrice = {
  includingVat: number;
  excludingVat: number;
};

export type SubProducts = {
  isMainProduct: boolean;
  product: Product;
  quantity: number;
};

type SecondChanceProduct = {
  id: string;
  salesPrice: SalesPrice | null;
};

export type SitemapLinks = {
  brandProductType?: NavigationNode;
  brandProductTypeSegment?: NavigationNode;
};

export type Breadcrumb = {
  path: string;
  label: string;
  indexable: boolean;
  contentType: string;
};

type NavigationNode = {
  label: string;
  path: string;
};

export type ExtendedUsp = {
  extendedUspId?: number;
  title: string;
  description: string;
  sortOrder?: number;
};

export type StoreAvailability = {
  stockLocationId: number;
  availabilityState: AvailabilityStates;
};

export type nextPickupOption = {
  pickupTime: string;
  isTomorrow: boolean;
  localizedDayName: string;
};

export enum AvailabilityState {
  AvailableSoon = 'AvailableSoon',
  OnStock = 'OnStock',
  TemporaryOutOfStock = 'TemporaryOutOfStock',
  OutOfStock = 'OutOfStock',
  Historic = 'Historic',
  PreOrder = 'PreOrder'
}

export enum AvailabilityStateAnalyticContext {
  AvailableSoon = 'available_soon',
  OnStock = 'on_stock',
  TemporaryOutOfStock = 'temporary_out_of_stock',
  OutOfStock = 'permanently_out_of_stock',
  Historic = 'no_stock_status_available',
  PreOrder = 'pre_order'
}

type InsuranceCondition = {
  id: string;
  description: string;
};

export type InsuranceLiability = {
  damage: number | null;
  theft: number | null;
};

type InsurancePrice = {
  includingVat: number;
  excludingVat: number;
};

export type InsuranceAttribute = {
  id: string;
  icon: string;
  name: string;
  description: string;
};

export type InsurancePackage = {
  title: string;
  products: InsuranceProduct[];
  usps: string[];
  liability: InsuranceLiability;
  isRecommended: boolean;
};

export enum InsurancePaymentType {
  ONE_TIME = 'one-time',
  MONTHLY = 'monthly'
}

export enum InsuranceType {
  INSURANCE = 'INSURANCE',
  EXTENDED_WARRANTY = 'EXTENDED_WARRANTY'
}

export enum InsuranceProvider {
  ERGO = 'ERGO',
  CoverGenius = 'CoverGenius'
}

export type InsuranceProduct = {
  id: string;
  title: string;
  paymentType: InsurancePaymentType;
  durationInYears: number;
  premium: InsurancePrice;
  yearlyPremium: InsurancePrice;
  usps: string[];
  isRecommended: boolean;
  insuranceType: InsuranceType;
};

export type Insurance = {
  description: string;
  documents: {
    insuranceCardUrl: string;
    termsAndConditionsUrl: string;
  };
  packages: InsurancePackage[];
  partner: InsuranceProvider;
  summary: {
    instructions: {
      title: string | null;
      description: string | null;
    };
    requiredAttributes: InsuranceAttribute[];
    optionalAttributes: InsuranceAttribute[];
    liability: InsuranceLiability;
    conditions: InsuranceCondition[];
    liabilityCondition: InsuranceCondition | null;
    usps: string[];
  };
  type: InsuranceType;
};

export type Alternative = {
  product: Product;
  distinctProperties: DistinctProperty[];
};

export type DistinctProperty = {
  displayName: string;
  hasExplanation: boolean;
  propertyId: string;
  value: string | ReactNode;
};

type CoolbluesChoice = {
  content: {
    title?: string;
    explanation?: string;
    explanationBullets?: string[];
    specialist?: {
      image: CDNImage;
    };
    type: 'Normal' | 'Green';
  };
};

type PromoBoxLocation = 'FIRST' | 'SECOND';

export type PromoBox = {
  id: number;
  location: PromoBoxLocation;
  image: CDNImage | null;
  textMarkdown: string;
  title: string;
};

export type PageLayout = {
  id: string;
  layouts: Array<Layout | null> | null;
};

export type Layout = {
  variationId: string;
  name: string;
  sections: LayoutSection[];
};

export type LayoutSection = {
  type: SectionType;
  article: Article | null;
};

type BlocksProps = ComponentProps<(typeof HemingwayBlocks)[keyof typeof HemingwayBlocks]>;

type Article = {
  id: string;
  blocks: BlocksProps[];
  criticalBlocks: string[];
};

export type SectionType =
  | 'MAIN_INFORMATION'
  | 'CONFIGURATOR'
  | 'PRODUCT_INFORMATION'
  | 'REVIEWS'
  | 'ALTERNATIVES'
  | 'ACCESSORY_CATEGORIES'
  | 'RULE_BASED_PROPOSITIONS'
  | 'INSURANCE_AND_SUBSCRIPTION'
  | 'SPECIFICATIONS'
  | 'ORDER_BLOCK_SUMMARY'
  | 'CALL_ME'
  | 'CONTACT_ME'
  | 'DETAILED_VISUAL_ENTRANCES'
  | 'FAQ'
  | 'INSPIRATIONAL'
  | 'MANUFACTURERS_SPECIFICATIONS'
  | 'NEWSLETTER'
  | 'STEPPED_PROPOSITIONS'
  | 'TEXT_IMAGE'
  | 'TEXT_IMAGE_2COLUMNS'
  | 'TEXT_IMAGE_3COLUMNS'
  | 'TEXT_VIDEO'
  | 'VIDEO';

export type Notice = {
  text: string;
  linkTitle: string;
  link: string;
};

export type PersonalPrice = {
  labelText: string;
  price: {
    includingVat: number;
    excludingVat?: number;
  };
};

export type PromotionalContent = {
  promoIcon?: PromotionalContentPromoIcon;
  promoLabel?: PromotionalContentPromoLabel;
};

export type PromotionalContentPromoIcon = {
  altText: string;
  image: {
    id: string;
    collectionName: CollectionName;
  };
  details?: PromotionalContentDetails;
};

export type PromotionalContentPromoLabel = {
  label: string;
  details?: PromotionalContentDetails;
};

export type PromotionalContentDetails = {
  title: string;
  explanation: string;
};

export type UrlAlternative = {
  slug: string;
  hrefLang: string;
};

export type ConLinkInformation = { conIndex: string; linkTitle: string };

export type Accessory = {
  productTypeId: number;
  product: {
    id: number;
    name: string;
    prices: {
      listPrice?: { includingVat: number } | null;
      msrp: { includingVat: number };
      salesPrice: { excludingVat: number; includingVat: number };
    };
    personalPrice?: PersonalPrice | null;
    shortName: string | null;
  };
};
